import {
  prisma,
  FormEntity,
  Prisma,
  ResourceType,
  ActionType,
  FormEntityType,
  TemplateFor,
} from "client";
import {
  getBookableItemByTicketId,
  getBookableItemIdsFromTicketIds,
} from "services/bookableItem";
import { getEvent } from "services/event";

const checkFloorPlanAndTypeExist = async (
  entities: { [key: string]: any }[]
) => {
  const isFloorPlanAndTypeExist = await prisma.formEntity.findFirst({
    where: {
      floorPlanId: {
        in: entities
          .filter((entity) => entity.floorPlanId)
          .map((entity) => entity.floorPlanId),
      },
      type: {
        in: entities.map((entity) => entity.type),
      },
    },
  });
  if (isFloorPlanAndTypeExist) {
    throw new Error("A form with this floor plan and type already exists.");
  }
};

// CREATE
export const createFormTemplate = async (
  userId: string,
  formTemplate: {
    whiteLabelId: string;
    createdById: string;
    eventId?: string;
    json?: any;
    text?: string;
  },
  formTemplateTranslations: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  entities: FormEntity[]
) => {
  try {
    await checkFloorPlanAndTypeExist(entities);
    // check if there is quick form template for this event
    if (entities.some((entity) => entity.type === FormEntityType.QUICK_FORM)) {
      const quickFormTemplate = await prisma.formTemplate.findFirst({
        where: {
          eventId: formTemplate.eventId,
          formEntities: {
            some: {
              type: FormEntityType.QUICK_FORM,
            },
          },
        },
      });
      if (quickFormTemplate) {
        throw new Error(
          "There is already a quick form template for this event"
        );
      }
    }

    const newFormTemplate = await prisma.formTemplate.create({
      data: {
        whiteLabel: {
          connect: {
            id: formTemplate.whiteLabelId,
          },
        },
        json: formTemplate.json || null,
        text: formTemplate.text || null,
        ...(formTemplate.eventId
          ? {
              event: {
                connect: {
                  id: formTemplate.eventId,
                },
              },
            }
          : {}),
        createdBy: {
          connect: {
            id: formTemplate.createdById,
          },
        },
        translations: {
          createMany: {
            data: formTemplateTranslations,
          },
        },

        formEntities: {
          createMany: {
            data: entities,
          },
        },
      },
    });
    return { formTemplate: newFormTemplate };
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};
export const createEventFormTemplate = async (
  userId: string,
  formTemplate: {
    whiteLabelId: string;
    eventId: string;
    createdById: string;
  },
  translations: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  formData: {
    id: string;
    [key: string]: any;
  },
  entity: FormEntity
) => {
  try {
    const newFormTemplate = await prisma.formTemplate.create({
      data: {
        whiteLabel: {
          connect: {
            id: formTemplate.whiteLabelId,
          },
        },
        createdBy: {
          connect: {
            id: formTemplate.createdById,
          },
        },
        event: { connect: { id: formTemplate.eventId } },
        translations: {
          createMany: {
            data: translations,
          },
        },
        formEntities: {
          createMany: {
            data: [entity],
          },
        },
      },
    });

    const { id, ...data } = formData;
    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id: newFormTemplate.id },
      data: {
        ...data,
      },
    });
    return { formTemplate: newFormTemplate };
  } catch (error: any) {
    throw new Error(error);
  }
};
export const createParticipantFormTemplate = async (
  formTemplate: {
    whiteLabelId: string;
    eventId: string;
    createdById: string;
  },
  translations: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  formData: {
    json: Prisma.InputJsonValue;
    numberOfQuestions?: number;
  },
  entity: FormEntity
) => {
  try {
    const existingFormEntity = await prisma.formEntity.findUnique({
      where: {
        entityID: entity.entityID || undefined,
      },
      include: {
        formTemplate: true,
      },
    });

    if (existingFormEntity?.formTemplate) {
      const updatedFormTemplate = await prisma.formTemplate.update({
        where: { id: existingFormEntity.formTemplate.id },
        data: {
          translations: {
            deleteMany: {},
            create: translations,
          },
        },
      });
      return { formTemplate: updatedFormTemplate };
    }
    const newFormTemplate = await prisma.formTemplate.create({
      data: {
        whiteLabel: {
          connect: {
            id: formTemplate.whiteLabelId,
          },
        },
        event: {
          connect: {
            id: formTemplate.eventId,
          },
        },
        createdBy: {
          connect: {
            id: formTemplate.createdById,
          },
        },
        translations: {
          createMany: {
            data: translations,
          },
        },
        formEntities: {
          create: entity,
        },
        json: formData.json,
        numberOfQuestions: formData.numberOfQuestions,
        text: JSON.stringify(formData.json),
      },
    });

    return { formTemplate: newFormTemplate };
  } catch (error: any) {
    console.error("Error in createParticipantFormTemplate:", error);
    throw error;
  }
};
export const createSponsorFormTemplate = async (
  formTemplate: {
    whiteLabelId: string;
    eventId: string;
    createdById: string;
  },
  translations: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  formData: {
    id: string;
    [key: string]: any;
  },
  entity: FormEntity
) => {
  try {
    const newFormTemplate = await prisma.formTemplate.create({
      data: {
        whiteLabel: {
          connect: {
            id: formTemplate.whiteLabelId,
          },
        },
        createdBy: {
          connect: {
            id: formTemplate.createdById,
          },
        },
        // event: { connect: { id: formTemplate.eventId } },
        translations: {
          createMany: {
            data: translations,
          },
        },
        formEntities: {
          createMany: {
            data: [entity],
          },
        },
      },
    });

    const { id, ...data } = formData;
    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id: newFormTemplate.id },
      data: {
        ...data,
      },
    });
    return { formTemplate: newFormTemplate };
  } catch (error: any) {
    throw new Error(error);
  }
};
export const createParticipantsFormTemplate = async (
  formTemplate: {
    eventId: string;
    createdById: string;
  },
  translations: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  formData: {
    id: string;
    [key: string]: any;
  },
  entity: FormEntity
) => {
  try {
    const event = await getEvent(formTemplate.eventId);
    if (!event?.whiteLabelId) {
      throw new Error("Event not found");
    }
    const existingFormEntity = await prisma.formEntity.findUnique({
      where: {
        entityID: entity.entityID || undefined,
      },
      include: {
        formTemplate: true,
      },
    });

    if (existingFormEntity?.formTemplate) {
      const updatedFormTemplate = await prisma.formTemplate.update({
        where: { id: existingFormEntity.formTemplate.id },
        data: {
          translations: {
            deleteMany: {},
            create: translations,
          },
        },
      });
      return { formTemplate: updatedFormTemplate };
    }
    const newFormTemplate = await prisma.formTemplate.create({
      data: {
        whiteLabel: {
          connect: {
            id: event.whiteLabelId,
          },
        },
        createdBy: {
          connect: {
            id: formTemplate.createdById,
          },
        },
        // event: { connect: { id: formTemplate.eventId } },
        translations: {
          createMany: {
            data: translations,
          },
        },
        formEntities: {
          create: entity,
        },
      },
    });

    return { formTemplate: newFormTemplate };
  } catch (error: any) {
    throw new Error(error);
  }
};
// READ
export const getInvitationFormTemplatesByEventId = async (
  eventId: string,
  locale: string
) => {
  try {
    const formTemplates = await prisma.formEntity.findMany({
      where: {
        formTemplate: {
          eventId,
        },
        type: FormEntityType.INVITATION,
      },
      include: {
        formTemplate: {
          include: {
            translations: {
              where: {
                language: {
                  code: locale,
                },
              },
            },
          },
        },
      },
    });
    return formTemplates;
  } catch (error) {
    return { error };
  }
};
export const getAllFormTemplates = async (userId: string) => {
  try {
    const formTemplates = await prisma.formTemplate.findMany();
    return { formTemplates };
  } catch (error) {
    return { error };
  }
};

export const getFormResultsByFormTemplate = async (
  userId: string,
  id: string
) => {
  try {
    const formResults = await prisma.formResult.findMany({
      where: { formTemplateId: id },
      include: {
        formResultEntity: true,
      },
    });
    return { data: formResults };
  } catch (error) {
    return { error };
  }
};

export const getFormTemplateById = async (id: string) => {
  try {
    const formTemplate = await prisma.formTemplate.findUnique({
      where: { id },
    });
    return { formTemplate };
  } catch (error) {
    return { error };
  }
};

export const getFormTemplatesByTicketIds = async (
  ticketIds: string[],
  lang: string
) => {
  try {
    const formTemplates = await prisma.formTemplate.findMany({
      where: {
        formEntities: {
          some: {
            entityID: { in: ticketIds },
          },
        },
      },

      include: {
        formEntities: true,
        translations: {
          include: {
            language: true,
          },
        },
      },
    });
    return formTemplates.map((formTemplate) => ({
      ...formTemplate,
      ...formTemplate.translations.find((t) => t.language.code === lang),
      id: formTemplate.id,
    }));
  } catch (error) {
    return { error };
  }
};
export const getFormTemplatesByTicketId = async (ticketId: string) => {
  try {
    const formTemplate = await prisma.formTemplate.findFirst({
      where: {
        formEntities: {
          some: {
            entityID: ticketId,
          },
        },
      },
    });
    return formTemplate;
  } catch (error) {
    return { error };
  }
};

export const getFormTemplatesByUser = async (userId: string) => {
  try {
    const formTemplates = await prisma.formTemplate.findMany({
      where: { createdById: userId },
    });
    return formTemplates;
  } catch (error) {
    return { error };
  }
};

export const getFormTemplatesByWhiteLabel = async (
  userId: string,
  whiteLabelId: string
) => {
  try {
    const formTemplates = await prisma.formTemplate.findMany({
      where: { whiteLabelId },
    });
    return { formTemplates };
  } catch (error) {
    return { error };
  }
};
export const getFormTemplatesByEventId = async (
  eventId: string,
  lang: string
) => {
  try {
    const formTemplate = await prisma.formTemplate.findFirst({
      where: {
        eventId,
        formEntities: {
          some: {
            entityID: eventId,
            type: FormEntityType.EVENT,
          },
        },
      },
    });

    return { formTemplate };
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getFormsTemplatesByEventId = async (
  eventId: string,
  lang: string
) => {
  try {
    const formTemplate = await prisma.formTemplate.findMany({
      where: {
        eventId,
        translations: {
          some: {
            language: {
              code: lang,
            },
          },
        },
      },
    });

    return formTemplate;
  } catch (error: any) {
    console.error(error);
    throw new Error(error);
  }
};
export const getExhibitorFormTemplates = async (
  exhibitorId: string,
  lang: string
) => {
  try {
    const formTemplate = await prisma.formTemplate.findFirst({
      where: {
        formEntities: {
          some: {
            type: FormEntityType.MEETING,
            entityID: exhibitorId,
          },
        },
        translations: {
          some: {
            language: {
              code: lang,
            },
          },
        },
      },
      include: {
        formEntities: true,
        translations: {
          where: {
            language: {
              code: lang,
            },
          },
        },
      },
    });

    return { formTemplate };
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getFormTemplatesbyEventLocalized = async (
  whiteLabelId: string,
  eventId: string,
  lang: string
) => {
  try {
    const formTemplates = await prisma.formTemplate.findMany({
      where: {
        whiteLabelId,
        formEntities: {
          some: {
            bookableItem: {
              eventId: eventId,
            },
          },
        },
        translations: {
          some: {
            language: {
              code: lang,
            },
          },
        },
      },
    });
    return { formTemplates };
  } catch (error) {
    return { error };
  }
};

export const getAllForms = async (userId: string) => {
  try {
    const formTemplates = await prisma.formTemplate.findMany({});
    return { formTemplates };
  } catch (error) {
    return { error };
  }
};

export const getAllFormsUnderEvent = async (eventId: string, lang: string) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id: eventId },
      include: {
        bookableItems: true,
        invitations: true,
        floorPlans: {
          select: {
            id: true,
          },
        },
      },
    });
    if (!event) {
      return { error: "Event not found" };
    }
    const formTemplates = await prisma.formTemplate.findMany({
      where: {
        OR: [
          {
            formEntities: {
              some: {
                OR: [
                  {
                    entityID: {
                      in: [
                        ...event.bookableItems.map((t) => t.id),
                        ...event.invitations.map((i) => i.id),
                      ],
                    },
                  },
                  {
                    floorPlanId: {
                      in: event.floorPlans.map((i) => i.id),
                    },
                  },
                ],
              },
            },
          },
          {
            event: {
              id: event.id,
            },
            formEntities: {
              some: {
                type: FormEntityType.QUICK_FORM,
              },
            },
          },
        ],
      },
      include: {
        formEntities: true, // Include related FormEntity records
        translations: {
          include: {
            language: true, // Include language details
          },
        },
      },
    });
    // Map the form templates to include the relevant translations
    return formTemplates.map((formTemplate) => ({
      ...formTemplate,
      ...formTemplate.translations.find((t) => t.language.code === lang),
      id: formTemplate.id,
    }));
  } catch (error) {
    return { error };
  }
};

export const getQuickFormTemplateByEventId = async (eventId: string) => {
  try {
    const quickFormTemplate = await prisma.formTemplate.findFirst({
      where: {
        eventId,
        formEntities: { some: { type: FormEntityType.QUICK_FORM } },
      },
    });
    return quickFormTemplate;
  } catch (error) {
    return { error };
  }
};

export const updateMeetingFormTemplate = async (
  userId: string,
  id: string,
  formData: {
    id: string;
    [key: string]: any;
  },
  formTemplateTranslations?: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  linkedIds?: { [key: string]: any }[]
) => {
  try {
    // delete old entities that are related to this
    if (linkedIds?.length) {
      await prisma.formEntity.deleteMany({
        where: { formTemplateId: formData.id },
      });
    }

    const withLinkedTickets = linkedIds?.length
      ? {
          formEntities: {
            createMany: {
              data: linkedIds.map((ticket) => ({
                entityID: ticket.entityID,
                type: "Ticket",
              })),
            },
          } as any,
        }
      : {};
    const { id, ...data } = formData;
    if (formTemplateTranslations) {
      const formTranslitions = await Promise.all(
        formTemplateTranslations.map(async (translation) => {
          return await prisma.formTemplateTranslations.update({
            where: { id: translation.id },
            data: { name: translation.name },
          });
        })
      );
    }
    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id },
      data: {
        ...data,
        ...withLinkedTickets,
      },
    });
    return { formTemplate: updatedFormTemplate };
  } catch (error) {
    console.log(error);
    return { error };
  }
};

// UPDATE
export const updateFormTemplate = async (
  userId: string,
  id: string,
  formData: {
    id: string;
    [key: string]: any;
  },
  formTemplateTranslations?: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[],
  linkedIds?: { [key: string]: any }[]
) => {
  try {
    // delete old entities that are related to this
    if (linkedIds?.length) {
      await prisma.formEntity.deleteMany({
        where: { formTemplateId: formData.id },
      });
    }

    await checkFloorPlanAndTypeExist(linkedIds ?? []);
    const withLinkedTickets = linkedIds?.length
      ? {
          formEntities: {
            createMany: {
              data: linkedIds,
            },
          } as any,
        }
      : {};
    const { id, ...data } = formData;
    if (formTemplateTranslations) {
      const formTranslitions = await Promise.all(
        formTemplateTranslations.map(async (translation) => {
          return await prisma.formTemplateTranslations.update({
            where: { id: translation.id },
            data: { name: translation.name },
          });
        })
      );
    }
    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id },
      data: {
        ...data,
        ...withLinkedTickets,
      },
    });
    return { formTemplate: updatedFormTemplate };
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};
export const updateEventFormTemplate = async (
  userId: string,
  id: string,
  formData: {
    id: string;
    [key: string]: any;
  },
  eventId: string,
  formTemplateTranslations?: Prisma.FormTemplateTranslationsCreateManyFormTemplateInput[]
) => {
  try {
    const { id, ...data } = formData;
    if (formTemplateTranslations) {
      const formTranslations = await Promise.all(
        formTemplateTranslations.map(async (translation) => {
          return await prisma.formTemplateTranslations.update({
            where: { id: translation.id },
            data: { name: translation.name },
          });
        })
      );
    }
    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id },
      data: {
        ...data,
        event: { connect: { id: eventId } },
      },
    });
    return { formTemplate: updatedFormTemplate };
  } catch (error) {
    return { error };
  }
};

// DELETE
export const deleteFormTemplate = async (userId: string, id: string) => {
  try {
    await prisma.$transaction(async (tx) => {
      await tx.formEntity.deleteMany({
        where: { formTemplateId: id },
      });
      await tx.formTemplateTranslations.deleteMany({
        where: { formTemplateId: id },
      });
      await tx.formTemplate.delete({
        where: { id },
      });
    });
    // Server Actions can only return JSON-serializable values to Client Components.
    // Prisma errors and some Prisma-returned types may not be serializable, so keep this minimal.
    return { ok: true as const, id };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const code =
      typeof (error as any)?.code === "string"
        ? ((error as any).code as string)
        : undefined;
    return {
      ok: false as const,
      error: {
        message,
        ...(code ? { code } : {}),
      },
    };
  }
};

// update form entities
export const addEntityToFormTemplate = async (
  userId: string,
  formTemplateId: string,
  entityData: FormEntity
) => {
  try {
    // create entity
    const newFormEntity = await prisma.formEntity.create({
      data: entityData,
    });

    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id: formTemplateId },
      data: {
        formEntities: {
          connect: { id: newFormEntity.id },
        },
      },
    });

    return updatedFormTemplate;
  } catch (error) {
    throw error;
  }
};

// remove entity from form template

export const removeEntityFromFormTemplate = async (
  userId: string,
  formTemplateId: string,
  entityId: string
) => {
  try {
    const updatedFormTemplate = await prisma.formTemplate.update({
      where: { id: formTemplateId },
      data: {
        formEntities: {
          disconnect: { id: entityId },
        },
      },
    });

    return updatedFormTemplate;
  } catch (error) {
    throw error;
  }
};

// get event formtemplate from formEntity by eventId
export const getEventFormTemplate = async (eventId: string) => {
  try {
    const response = await prisma.formTemplate.findFirst({
      where: {
        formEntities: {
          some: {
            entityID: eventId,
          },
        },
      },
    });

    return response;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};
export const getEventFormTemplates = async (eventIds: string[]) => {
  try {
    const response = await prisma.formTemplate.findMany({
      where: {
        formEntities: {
          some: {
            entityID: {
              in: eventIds,
            },
          },
        },
      },
    });

    return response;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};
export const getEventsFormTemplates = async (eventIds: string[]) => {
  try {
    const response = await prisma.formTemplate.findMany({
      where: {
        formEntities: {
          some: {
            entityID: {
              in: eventIds,
            },
          },
        },
      },
    });

    return response;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};
/**
 * Resolves FormEntity(bookableItemId, formTemplateId) first, then loads templates by id.
 * `formTemplate.findMany({ where: { formEntities: { some: … } } })` scans FormTemplate and
 * runs a FormEntity $lookup per template (COLLSCAN in the slow-query log).
 */
const getFormTemplateIdsByBookableItemIds = async (
  bookableItemIds: string[]
): Promise<string[]> => {
  if (bookableItemIds.length === 0) return [];
  const formEntities = await prisma.formEntity.findMany({
    where: {
      bookableItemId: { in: bookableItemIds },
      formTemplateId: { not: null },
    },
    select: { formTemplateId: true },
  });
  return [
    ...new Set(
      formEntities
        .map((entity) => entity.formTemplateId)
        .filter((id): id is string => Boolean(id))
    ),
  ];
};

export const getTicketsFormTemplates = async (ticketIds: string[]) => {
  try {
    const bookableItems = await getBookableItemIdsFromTicketIds(ticketIds);
    const formTemplateIds =
      await getFormTemplateIdsByBookableItemIds(bookableItems);
    if (formTemplateIds.length === 0) return [];
    const formTemplates = await prisma.formTemplate.findMany({
      where: { id: { in: formTemplateIds } },
    });

    return formTemplates;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};
export const getInvitationsFormTemplates = async (invitationIds: string[]) => {
  try {
    const response = await prisma.formTemplate.findMany({
      where: {
        formEntities: {
          some: {
            type: FormEntityType.INVITATION,
            entityID: {
              in: invitationIds,
            },
          },
        },
      },
    });

    return response;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};
// get event formtemplate from formEntity by event slug
export const getEventFormTemplateBySlug = async (slug: string) => {
  try {
    const event = await prisma.event.findFirst({
      where: {
        slug,
      },
    });

    if (!event) {
      return {
        error: "No event found",
      };
    }

    const response = await prisma.formTemplate.findFirst({
      where: {
        formEntities: {
          some: {
            entityID: event.id,
            type: FormEntityType.EVENT,
          },
        },
      },
    });
    return response;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};

// get ticket formtemplate from formEntity by ticketId
export const getTicketFormTemplate = async (ticketId: string) => {
  try {
    const bookableItem = await getBookableItemByTicketId(ticketId, {
      select: { id: true },
    });
    // Previously `bookableItemId: undefined` dropped the filter and matched any template.
    if (!bookableItem) return null;
    const [formTemplateId] = await getFormTemplateIdsByBookableItemIds([
      bookableItem.id,
    ]);
    if (!formTemplateId) return null;
    const response = await prisma.formTemplate.findUnique({
      where: { id: formTemplateId },
    });

    return response;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};

// duplicate form template with new name and empty formentities
export const duplicateFormTemplate = async (
  userId: string,
  formTemplateId: string
) => {
  try {
    const response = await prisma.formTemplate.findUnique({
      where: {
        id: formTemplateId,
      },
      include: {
        formEntities: true,
        translations: true,
      },
    });

    if (!response) {
      return {
        error: "No form template found",
      };
    }

    const newFormTemplate = await prisma.formTemplate.create({
      data: {
        whiteLabel: {
          connect: {
            id: response.whiteLabelId as string,
          },
        },
        createdBy: response.createdById
          ? {
              connect: {
                id: response.createdById,
              },
            }
          : undefined,
        json: response.json,
        text: response.text,
        formEntities: undefined,
        translations: {
          createMany: {
            data: response.translations,
          },
        },
      },
    });

    return newFormTemplate;
  } catch (error: any) {
    return {
      error: error.message,
    };
  }
};

//  get form json keys from form template by entity id
export const getFormJsonKeys = async (
  // userId: string,
  entityId: string
) => {
  try {
    const response = await prisma.formTemplate.findFirst({
      where: {
        formEntities: {
          some: {
            entityID: entityId,
          },
        },
      },
    });

    if (!response) {
      return [];
    }

    const json = response.json as any;
    const allElementNames: string[] = [];

    json?.pages?.forEach((page: any) => {
      allElementNames.push(...extractElementNames(page));
    });

    return allElementNames;
  } catch (error: any) {
    throw error;
  }
};
//  get form json keys from form template by entity ids
export const getFormJsonKeysByEntityIds = async (
  // userId: string,
  entityIds: string[]
) => {
  try {
    const response = await prisma.formTemplate.findMany({
      where: {
        formEntities: {
          some: {
            entityID: {
              in: entityIds,
            },
          },
        },
      },
    });

    if (!response) {
      return [];
    }

    const allElementNames: string[] = [];

    response.forEach((formTemplate) => {
      const json = formTemplate.json as any;

      json?.pages?.forEach((page: any) => {
        allElementNames.push(...extractElementNames(page));
      });
    });
    //  remove duplicates

    return allElementNames;
  } catch (error: any) {
    throw error;
  }
};
export const getFormJsonKeysByContactBookGroupIds = async (eventId: string) => {
  try {
    const contactBookGroups = await prisma.contactBookGroup.findMany({
      where: { eventId },
    });

    const response = await prisma.formTemplate.findMany({
      where: {
        OR: [
          { eventId },
          {
            formEntities: {
              some: {
                entityID: { in: contactBookGroups.map((c) => c.id) },
              },
            },
          },
        ],
      },
    });

    if (!response) return [];

    const allElementNames: string[] = [];
    response.forEach((formTemplate) => {
      const json = formTemplate.json as any;
      json?.pages?.forEach((page: any) => {
        allElementNames.push(...extractElementNames(page));
      });
    });

    const uniqueElementNames = [...new Set(allElementNames)];

    return uniqueElementNames.map((key) =>
      key.startsWith("custom_") ? `extraData.${key}` : `${key}`
    );
  } catch (error: any) {
    throw error;
  }
};

export const getFormJsonKeysByEventId = async (
  // userId: string,
  eventId: string
) => {
  try {
    const tickets = await prisma.ticket.findMany({
      where: {
        eventId,
      },
    });
    const venues = await prisma.venue.findMany({
      where: {
        eventIds: {
          has: eventId,
        },
      },
    });
    const exhibitors = await prisma.exhibitor.findMany({
      where: {
        eventId,
      },
    });
    const sponsors = await prisma.sponsor.findMany({
      where: {
        eventId,
      },
    });
    const invitations = await prisma.invitation.findMany({
      where: {
        eventId,
      },
    });
    const tasks = await prisma.task.findMany({
      where: {
        eventId,
      },
    });

    const response = await prisma.formTemplate.findMany({
      where: {
        OR: [
          { eventId },
          {
            formEntities: {
              some: {
                entityID: {
                  in: tickets
                    .map((ticket) => ticket.id)
                    .concat(
                      venues.map((venue) => venue.id),
                      exhibitors.map((exhibitor) => exhibitor.id),
                      sponsors.map((sponsor) => sponsor.id),
                      invitations.map((invitation) => invitation.id),
                      tasks.map((task) => task.id),
                      eventId
                    ),
                },
              },
            },
          },
        ],
      },
    });

    if (!response) {
      return [];
    }

    const allElementNames: string[] = [];

    response.forEach((formTemplate) => {
      const json = formTemplate.json as any;

      json?.pages?.forEach((page: any) => {
        allElementNames.push(...extractElementNames(page));
      });
    });

    //  remove duplicates
    const uniqueElementNames = [...new Set(allElementNames)];

    return uniqueElementNames;
  } catch (error: any) {
    throw error;
  }
};

function extractElementNames(page: any): string[] {
  const elementNames: string[] = [];
  page.elements?.forEach((element: any) => {
    if ("name" in element) {
      elementNames.push(element.name);
    }
    if (element.type === "panel") {
      elementNames.push(...extractElementNames(element));
    }
  });
  return elementNames;
}

function extractElementNamesWithAllValues(page: any): any[] {
  const elementNames: any[] = [];
  page.elements?.forEach((element: any) => {
    elementNames.push(element);
  });
  return elementNames;
}

export const transformSurveyResult = (formTemplate: any, surveyResult: any) => {
  const readableResult: any = {};
  console.log(surveyResult);
  if (!formTemplate && surveyResult) {
    // return surveyResult values as readableResult[displayKey] = {
    //   title: title || name,
    Object.keys(surveyResult).forEach((key) => {
      readableResult[key] = {
        title: key,
        key,
        type: "text",
        value: surveyResult[key],
      };
    });
    return readableResult;
  }
  const elements =
    formTemplate?.pages?.flatMap((page: any) => page.elements) || [];

  elements.forEach((element: any) => {
    const { name, type, title, choices } = element;
    const response = surveyResult[name];

    if (response !== undefined) {
      const displayKey = name || title;
      let value;

      if (type === "checkbox" && Array.isArray(response) && choices) {
        console.log("checkbox");
        value = response.map(
          (val) =>
            choices.find((choice: any) => choice.value === val)?.text || val
        );
      } else if (type === "dropdown" && Array.isArray(response)) {
        value = response.map((val) => {
          const match = choices.find((choice: any) => choice.value === val.id);
          return { id: val.id, label: match ? match.text : val.label };
        })[0];
      } else if (type === "file" && Array.isArray(response)) {
        value = response.map((file: any) => ({
          name: file.name,
          type: file.type,
          content: file.content,
        }));
      } else if (typeof response === "boolean") {
        value = response ? "Yes" : "No";
      } else if (choices && type !== "dropdown") {
        const choiceText = choices.find(
          (choice: any) => choice.value === response
        )?.text;
        value = choiceText
          ? `${choiceText}  ${surveyResult[name + "-Comment"] || ""}`
          : `${response} ${surveyResult[name + "-Comment"] || ""}`;
      } else {
        value = response;
      }

      readableResult[displayKey] = {
        title: title || name,
        key: name,
        type,
        value,
      };
    }
  });

  return readableResult;
};

export const getEventFormsFieldsAsUnique = async (eventId: string) => {
  const tickets = await prisma.ticket.findMany({
    where: { eventId: eventId },
    select: { id: true },
  });
  const ticketIds = tickets.map((t) => t.id);

  const formTemplates = await prisma.formTemplate.findMany({
    where: {
      OR: [
        {
          formEntities: {
            some: {
              type: FormEntityType.TICKET,
              entityID: { in: ticketIds },
            },
          },
        },
        {
          eventId: eventId,
        },
      ],
    },
  });

  const allElementNames: any[] = [];

  formTemplates.forEach((formTemplate) => {
    const json = formTemplate.json as any;
    json?.pages?.forEach((page: any) => {
      allElementNames.push(...extractElementNamesWithAllValues(page));
    });
  });

  // remove duplicates by name
  const uniqueElementNames = allElementNames.filter(
    (element: any, index: number, self: any[]) =>
      index === self.findIndex((t: any) => t.name === element.name)
  );

  return uniqueElementNames;
};

export const isEventHaveTickeformOrGeneralForm = async (eventId: string) => {
  const tickets = await prisma.ticket.findMany({
    where: { eventId: eventId },
    select: { id: true },
  });
  const ticketIds = tickets.map((t) => t.id);
  const ticketsFormTemplates = await prisma.formTemplate.findMany({
    where: {
      whiteLabel: {
        events: {
          some: {
            id: eventId,
          },
        },
      },
      formEntities: {
        some: {
          bookableItem: {
            ticket: {
              id: { in: ticketIds },
            },
          },
        },
      },
    },
    select: {
      id: true,
      formEntities: {
        select: {
          bookableItem: {
            select: {
              id: true,
              ticket: {
                select: {
                  id: true,
                },
              },
            },
          },
        },
      },
    },
  });
  const formTemplatesTicketsIds = ticketsFormTemplates
    .map((t) => t.formEntities.map((f) => f.bookableItem?.ticket?.id))
    .flat()
    .filter((id) => id);
  const { formTemplate } = await getFormTemplatesByEventId(eventId, "en");
  const floorplanWithoutForm = await prisma.floorPlan.findMany({
    where: {
      eventId: eventId,
      venue: {
        deletedAt: null,
      },
      deletedAt: null,
      formEntities: {
        none: {
          type: FormEntityType.FLOOR_PLAN_BOOKING,
        },
      },
    },
  });
  return (
    (ticketIds.length === formTemplatesTicketsIds.length &&
      floorplanWithoutForm.length === 0) ||
    formTemplate
  );
};
